export type SupportedUploadContentTypes = Record<string, string[]>;

export const PROFILE_IMAGE_UPLOAD_PURPOSE = 'profile-image';

export class UnsupportedS3UploadFileTypeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnsupportedS3UploadFileTypeError';
  }
}

export const SUPPORTED_S3_UPLOAD_CONTENT_TYPES: SupportedUploadContentTypes = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx'
  ],
  'application/msword': ['.doc'],
  'text/plain': ['.txt'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/rtf': ['.rtf'],
  'text/rtf': ['.rtf']
};

export const SUPPORTED_PROFILE_IMAGE_CONTENT_TYPES: SupportedUploadContentTypes =
  {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg']
  };

export function normalizeS3UploadContentType(contentType: string) {
  return contentType.trim().toLowerCase().split(';')[0] ?? '';
}

function getFileExtension(fileName: string) {
  const extension = fileName.split('.').pop();
  return extension ? `.${extension.toLowerCase()}` : '';
}

export function getSupportedUploadFileTypesLabel(
  supportedContentTypes: SupportedUploadContentTypes = SUPPORTED_S3_UPLOAD_CONTENT_TYPES
) {
  return Array.from(new Set(Object.values(supportedContentTypes).flat()))
    .sort()
    .join(', ');
}

export function getUploadInputAcceptString(
  supportedContentTypes: SupportedUploadContentTypes = SUPPORTED_S3_UPLOAD_CONTENT_TYPES
) {
  return [
    ...Object.keys(supportedContentTypes),
    ...Array.from(new Set(Object.values(supportedContentTypes).flat()))
  ].join(',');
}

export function isSupportedS3UploadFile(
  file: File,
  supportedContentTypes: SupportedUploadContentTypes = SUPPORTED_S3_UPLOAD_CONTENT_TYPES
) {
  const allowedExtensions =
    supportedContentTypes[normalizeS3UploadContentType(file.type)];
  return !!allowedExtensions?.includes(getFileExtension(file.name));
}

export function assertSupportedS3UploadFiles(
  files: FileList | File[],
  supportedContentTypes: SupportedUploadContentTypes = SUPPORTED_S3_UPLOAD_CONTENT_TYPES
) {
  const unsupportedFile = Array.from(files).find(
    (file) => !isSupportedS3UploadFile(file, supportedContentTypes)
  );

  if (unsupportedFile) {
    throw new UnsupportedS3UploadFileTypeError(
      `Unsupported file type for "${unsupportedFile.name}". Supported file types are: ${getSupportedUploadFileTypesLabel(
        supportedContentTypes
      )}.`
    );
  }
}

export function getS3UploadErrorMessage(error: unknown, fallback: string) {
  return error instanceof UnsupportedS3UploadFileTypeError
    ? error.message
    : fallback;
}
